import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { TextInput, SwitchInput } from '@/components/atoms'
import { OrderNoteModel } from '@/types'
import { OrderNoteFormState, orderNoteFieldsConfig } from './orderNoteFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface OrderNoteDialogProps {
    open: boolean
    mode: 'create' | 'edit' | 'view'
    editOrderNote: OrderNoteModel.OrderNote | null
    closeDialog: () => void
    onCreate: (data: OrderNoteFormState) => Promise<void>
    onUpdate: (id: string, data: Partial<OrderNoteFormState>) => Promise<void>
    loading: boolean
}

export const OrderNoteDialog = ({
    open,
    mode,
    editOrderNote,
    closeDialog,
    onCreate,
    onUpdate,
    loading = false,
}: OrderNoteDialogProps) => {
    const {
        handleSubmit,
        reset,
        formState: { errors },
        control,
        watch,
    } = useForm<OrderNoteFormState>({
        defaultValues: {
            content: '',
            status: 'active',
        },
    })

    const formValues = watch()
    const { fields } = orderNoteFieldsConfig({ formValues })

    useEffect(() => {
        if ((mode === 'edit' || mode === 'view') && editOrderNote) {
            reset({
                content: editOrderNote.content,
                status: editOrderNote.status,
            })
        } else {
            reset({
                content: '',
                status: 'active',
            })
        }
    }, [mode, editOrderNote, reset, open])

    const onSubmit = async (values: OrderNoteFormState) => {
        if (mode === 'create') {
            await onCreate(values)
        } else if (mode === 'edit' && editOrderNote) {
            await onUpdate(editOrderNote._id, values)
        }
    }

    useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

    return (
        <Dialog open={open} onOpenChange={closeDialog}>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Add note' : mode === 'edit' ? 'Edit note' : 'Note details'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Save a note you can attach to orders in one click.'
                            : mode === 'edit'
                                ? 'Change the note text or its status.'
                                : 'Details of this preset note.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <TextInput
                        control={control}
                        name="content"
                        label="Note"
                        placeholder="e.g. Customer did not respond to verification call..."
                        disabled={mode === 'view' || loading}
                        errors={errors}
                        rules={fields.content.rules}
                        multiline
                        rows={5}
                    />

                    <SwitchInput
                        control={control}
                        name="status"
                        label="Status"
                        description={formValues.status === 'active' ? 'Note is active' : 'Note is inactive'}
                        disabled={mode === 'view' || loading}
                        activeValue="active"
                        inactiveValue="inactive"
                        rules={fields.status.rules}
                    />
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
                        {mode === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {mode !== 'view' && (
                        <Button
                            type="button"
                            disabled={loading}
                            onClick={handleSubmit(onSubmit)}>
                            {loading && <Spinner className="size-4" />}
                            {mode === 'create' ? 'Add note' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
